import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type FormalNarrationTimeCue,
  type ImmersiveKnowledgeNarrationAnchor,
  type ImmersiveKnowledgeNarrationPage,
  resolveSpeechNarrationText,
} from "../narration/narrationPage";
import styles from "./ImmersiveKnowledgeNarration.module.css";

export {
  type FormalNarrationSource,
  type FormalNarrationTimeCue,
  type ImmersiveKnowledgeNarrationAnchor,
  type ImmersiveKnowledgeNarrationPage,
  immersiveKnowledgeNarrationAnchors,
} from "../narration/narrationPage";

export interface ImmersiveKnowledgeNarrationProps {
  /** Must change when the active knowledge node changes. A change cancels all old speech. */
  readonly contentKey: string;
  readonly title: string;
  readonly pages: readonly ImmersiveKnowledgeNarrationPage[];
  readonly anchor?: ImmersiveKnowledgeNarrationAnchor | undefined;
  readonly eyebrow?: string | undefined;
  readonly initialVolume?: number | undefined;
  readonly reducedMotion?: boolean | undefined;
  readonly onPageChange?: (pageIndex: number, page: ImmersiveKnowledgeNarrationPage) => void;
  /** Returns to the previous knowledge node. Omit for the first node in a scene. */
  readonly onPreviousKnowledgePoint?: (() => void) | undefined;
  readonly onComplete: () => void;
}

type NarrationStatus =
  | "idle"
  | "starting"
  | "playing"
  | "paused"
  | "ended"
  | "blocked"
  | "unavailable"
  | "error";

type NarrationMode = "formal" | "preview";

interface SpeechRuntime {
  readonly synthesis: SpeechSynthesis;
  readonly Utterance: typeof SpeechSynthesisUtterance;
}

const speechSynthesisGesturePrimedEvent = "history-sim:speech-synthesis-gesture-primed";
const primedSpeechSynthesisRuntimes = new WeakSet<SpeechSynthesis>();
const speechStartWatchdogMs = 1_200;
const formalAudioStartWatchdogMs = 8_000;

const anchorClassNames: Record<ImmersiveKnowledgeNarrationAnchor, string> = {
  "upper-left": styles.upperLeft ?? "",
  "upper-right": styles.upperRight ?? "",
  "middle-left": styles.middleLeft ?? "",
  "middle-right": styles.middleRight ?? "",
  "lower-left": styles.lowerLeft ?? "",
  "lower-right": styles.lowerRight ?? "",
};

function clampVolume(value: number) {
  if (!Number.isFinite(value)) return 0.8;
  return Math.min(1, Math.max(0, value));
}

function getSpeechRuntime(): SpeechRuntime | null {
  if (typeof window === "undefined") return null;

  const synthesis = window.speechSynthesis;
  const Utterance = window.SpeechSynthesisUtterance;
  if (!synthesis || typeof Utterance !== "function") return null;

  return { synthesis, Utterance };
}

/**
 * Unlocks gesture-gated speech engines without producing audible output.
 * This must run synchronously inside the first pointer, touch or keyboard event.
 */
export function primeSpeechSynthesisFromGesture() {
  const runtime = getSpeechRuntime();
  if (!runtime) return false;

  if (primedSpeechSynthesisRuntimes.has(runtime.synthesis)) {
    window.dispatchEvent(new Event(speechSynthesisGesturePrimedEvent));
    return true;
  }

  try {
    if (!runtime.synthesis.speaking && !runtime.synthesis.pending) {
      const primer = new runtime.Utterance("\u200B");
      primer.lang = "zh-CN";
      primer.rate = 10;
      primer.pitch = 1;
      primer.volume = 0;
      runtime.synthesis.speak(primer);
      try {
        runtime.synthesis.cancel();
        runtime.synthesis.resume();
      } catch {
        // The zero-volume utterance is harmless if queue cleanup is unavailable.
      }
    }

    primedSpeechSynthesisRuntimes.add(runtime.synthesis);
    window.dispatchEvent(new Event(speechSynthesisGesturePrimedEvent));
    return true;
  } catch {
    return false;
  }
}

/** Registers the one-time capture bridge before a learner enters a scene. */
export function installSpeechSynthesisGesturePrime() {
  if (typeof window === "undefined") return () => undefined;

  let active = true;
  const removeListeners = () => {
    if (!active) return;
    active = false;
    window.removeEventListener("pointerdown", handleGesture, true);
    window.removeEventListener("touchstart", handleGesture, true);
    window.removeEventListener("keydown", handleGesture, true);
  };
  const handleGesture = () => {
    if (primeSpeechSynthesisFromGesture()) removeListeners();
  };

  window.addEventListener("pointerdown", handleGesture, { capture: true, passive: true });
  window.addEventListener("touchstart", handleGesture, { capture: true, passive: true });
  window.addEventListener("keydown", handleGesture, { capture: true, passive: true });

  return removeListeners;
}

function preferredMandarinVoice(voices: readonly SpeechSynthesisVoice[]) {
  const normalizedLanguage = (voice: SpeechSynthesisVoice) =>
    voice.lang.trim().toLowerCase().replaceAll("_", "-");

  return (
    voices.find((voice) => normalizedLanguage(voice) === "zh-cn") ??
    voices.find((voice) => normalizedLanguage(voice) === "zh-hans-cn")
  );
}

function hasFormalNarration(page: ImmersiveKnowledgeNarrationPage | undefined) {
  return Boolean(page?.formalAudio?.src.trim());
}

/** Resolves an active, valid cue without trusting source order or malformed timings. */
export function formalNarrationCueAt(
  cues: readonly FormalNarrationTimeCue[] | undefined,
  currentTimeMs: number,
) {
  if (!Number.isFinite(currentTimeMs) || currentTimeMs < 0 || !cues?.length) return null;

  let activeCue: FormalNarrationTimeCue | null = null;
  for (const cue of cues) {
    if (
      !cue.id.trim() ||
      !cue.text.trim() ||
      !Number.isFinite(cue.startMs) ||
      !Number.isFinite(cue.endMs) ||
      cue.startMs < 0 ||
      cue.endMs <= cue.startMs ||
      currentTimeMs < cue.startMs ||
      currentTimeMs >= cue.endMs
    ) {
      continue;
    }

    if (!activeCue || cue.startMs >= activeCue.startMs) activeCue = cue;
  }

  return activeCue;
}

function statusCopy(status: NarrationStatus) {
  switch (status) {
    case "starting":
      return "正在自动启动本页普通话朗读";
    case "playing":
      return "正在朗读本页知识点";
    case "paused":
      return "朗读已暂停";
    case "ended":
      return "本页朗读结束，可继续研读或进入下一页";
    case "blocked":
      return "浏览器正在等待首次触碰；触碰页面任意位置后将自动朗读";
    case "unavailable":
      return "当前浏览器无法提供系统朗读，文字讲授仍可继续";
    case "error":
      return "朗读未能启动，文字讲授和后续任务不受影响";
    default:
      return "可播放本页普通话朗读";
  }
}

function formalStatusCopy(status: NarrationStatus) {
  switch (status) {
    case "starting":
      return "正在加载本页正式普通话音轨";
    case "playing":
      return "正在播放本页正式普通话音轨";
    case "paused":
      return "正式音轨已暂停";
    case "ended":
      return "正式音轨播放结束，可继续研读或进入下一页";
    case "unavailable":
      return "正式音轨未能播放，且当前浏览器无法提供系统朗读；文字讲授仍可继续";
    case "error":
      return "正式音轨未能播放，文字讲授和后续任务不受影响";
    default:
      return "可播放本页正式普通话音轨";
  }
}

export function ImmersiveKnowledgeNarration({
  contentKey,
  title,
  pages,
  anchor = "middle-right",
  eyebrow = "知识点讲授",
  initialVolume = 0.8,
  reducedMotion = false,
  onPageChange,
  onPreviousKnowledgePoint,
  onComplete,
}: ImmersiveKnowledgeNarrationProps) {
  const [cursor, setCursor] = useState({ contentKey, pageIndex: 0 });
  const [volume, setVolume] = useState(() => clampVolume(initialVolume));
  const [status, setStatus] = useState<NarrationStatus>(() =>
    hasFormalNarration(pages[0]) || getSpeechRuntime() ? "idle" : "unavailable",
  );
  const [narrationMode, setNarrationMode] = useState<NarrationMode>(() =>
    hasFormalNarration(pages[0]) ? "formal" : "preview",
  );
  const [formalAudioFallback, setFormalAudioFallback] = useState(false);
  const [activeFormalCue, setActiveFormalCue] = useState<FormalNarrationTimeCue | null>(null);
  const [completedPageIds, setCompletedPageIds] = useState<ReadonlySet<string>>(() => new Set());
  const [completionSent, setCompletionSent] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const formalAudioFailureRef = useRef<(() => void) | null>(null);
  const autoplayTargetRef = useRef<{
    readonly token: string;
    readonly page: ImmersiveKnowledgeNarrationPage;
  } | null>(null);
  const speechStartWatchdogRef = useRef<number | null>(null);
  const formalAudioStartWatchdogRef = useRef<number | null>(null);
  const pageLifecycleActiveRef = useRef(typeof document === "undefined" ? true : !document.hidden);
  const completionSentRef = useRef(false);
  const statusRef = useRef<NarrationStatus>(status);
  const narrationModeRef = useRef<NarrationMode>(narrationMode);
  const volumeRef = useRef(volume);

  const pageIndex =
    cursor.contentKey === contentKey
      ? Math.min(cursor.pageIndex, Math.max(pages.length - 1, 0))
      : 0;
  const currentPage = pages[pageIndex];
  const firstPageHasFormalNarration = hasFormalNarration(pages[0]);
  const autoplayToken = currentPage
    ? `${contentKey}:${pageIndex}:${currentPage.id}`
    : `${contentKey}:empty`;
  autoplayTargetRef.current = currentPage ? { token: autoplayToken, page: currentPage } : null;
  const activeAnchor = currentPage?.anchor ?? anchor;
  const formalAudioAvailable = hasFormalNarration(currentPage);
  const narrationAvailable = formalAudioAvailable || status !== "unavailable";

  const updateStatus = useCallback((nextStatus: NarrationStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const updateNarrationMode = useCallback((nextMode: NarrationMode) => {
    narrationModeRef.current = nextMode;
    setNarrationMode(nextMode);
  }, []);

  const clearSpeechStartWatchdog = useCallback(() => {
    if (speechStartWatchdogRef.current === null) return;
    window.clearTimeout(speechStartWatchdogRef.current);
    speechStartWatchdogRef.current = null;
  }, []);

  const clearFormalAudioStartWatchdog = useCallback(() => {
    if (formalAudioStartWatchdogRef.current === null) return;
    window.clearTimeout(formalAudioStartWatchdogRef.current);
    formalAudioStartWatchdogRef.current = null;
  }, []);

  const disposeFormalAudio = useCallback((audio: HTMLAudioElement) => {
    audio.onplaying = null;
    audio.onpause = null;
    audio.ontimeupdate = null;
    audio.onseeked = null;
    audio.onended = null;
    audio.onerror = null;

    try {
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute("src");
      audio.load();
    } catch {
      // Releasing a failed decoder must never hold navigation or fallback text.
    }
  }, []);

  const cancelNarration = useCallback(
    (shouldUpdateStatus: boolean) => {
      clearSpeechStartWatchdog();
      clearFormalAudioStartWatchdog();
      utteranceRef.current = null;
      formalAudioFailureRef.current = null;

      const audio = audioRef.current;
      audioRef.current = null;
      if (audio) disposeFormalAudio(audio);
      setActiveFormalCue(null);

      try {
        getSpeechRuntime()?.synthesis.cancel();
      } catch {
        // Cancellation failure must never hold the learner inside a knowledge node.
      }

      if (shouldUpdateStatus) {
        updateStatus(
          hasFormalNarration(autoplayTargetRef.current?.page) || getSpeechRuntime()
            ? "idle"
            : "unavailable",
        );
      }
    },
    [clearFormalAudioStartWatchdog, clearSpeechStartWatchdog, disposeFormalAudio, updateStatus],
  );

  useEffect(() => {
    volumeRef.current = volume;
    if (utteranceRef.current) {
      utteranceRef.current.volume = volume;
    }
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    setVolume(clampVolume(initialVolume));
  }, [initialVolume]);

  useEffect(() => {
    cancelNarration(false);
    completionSentRef.current = false;
    setCompletionSent(false);
    setCompletedPageIds(new Set());
    setCursor({ contentKey, pageIndex: 0 });
    setFormalAudioFallback(false);
    updateNarrationMode(firstPageHasFormalNarration ? "formal" : "preview");
    updateStatus(firstPageHasFormalNarration || getSpeechRuntime() ? "idle" : "unavailable");
  }, [cancelNarration, contentKey, firstPageHasFormalNarration, updateNarrationMode, updateStatus]);

  useEffect(
    () => () => {
      cancelNarration(false);
    },
    [cancelNarration],
  );

  const markPageComplete = useCallback((pageId: string) => {
    setCompletedPageIds((current) => {
      if (current.has(pageId)) return current;
      const next = new Set(current);
      next.add(pageId);
      return next;
    });
  }, []);

  const playSpeechPreview = useCallback(
    (page: ImmersiveKnowledgeNarrationPage, isFormalFallback = false) => {
      const runtime = getSpeechRuntime();
      cancelNarration(false);
      updateNarrationMode("preview");
      setFormalAudioFallback(isFormalFallback);
      if (!runtime) {
        utteranceRef.current = null;
        updateStatus("unavailable");
        return;
      }

      try {
        try {
          // Some engines remain globally paused after a cancelled prior page.
          runtime.synthesis.resume();
        } catch {
          // A failed defensive resume does not prevent a fresh speak attempt.
        }

        const utterance = new runtime.Utterance(resolveSpeechNarrationText(page));
        utterance.lang = "zh-CN";
        utterance.rate = 0.92;
        utterance.pitch = 1;
        utterance.volume = volumeRef.current;

        const preferredVoice = preferredMandarinVoice(runtime.synthesis.getVoices());
        if (preferredVoice) utterance.voice = preferredVoice;

        utterance.onstart = () => {
          if (utteranceRef.current !== utterance) return;
          clearSpeechStartWatchdog();
          updateStatus("playing");
        };
        utterance.onpause = () => {
          if (utteranceRef.current === utterance) updateStatus("paused");
        };
        utterance.onresume = () => {
          if (utteranceRef.current === utterance) updateStatus("playing");
        };
        utterance.onend = () => {
          if (utteranceRef.current !== utterance) return;
          clearSpeechStartWatchdog();
          utteranceRef.current = null;
          markPageComplete(page.id);
          updateStatus("ended");
        };
        utterance.onerror = (event) => {
          if (utteranceRef.current !== utterance) return;
          clearSpeechStartWatchdog();
          utteranceRef.current = null;
          updateStatus(event.error === "not-allowed" ? "blocked" : "error");
        };

        utteranceRef.current = utterance;
        updateStatus("starting");
        speechStartWatchdogRef.current = window.setTimeout(() => {
          if (utteranceRef.current !== utterance || statusRef.current !== "starting") return;
          utteranceRef.current = null;
          try {
            runtime.synthesis.cancel();
            runtime.synthesis.resume();
          } catch {
            // The blocked state still exposes text, navigation and manual replay.
          }
          updateStatus("blocked");
        }, speechStartWatchdogMs);
        runtime.synthesis.speak(utterance);
      } catch {
        clearSpeechStartWatchdog();
        utteranceRef.current = null;
        updateStatus("error");
      }
    },
    [
      cancelNarration,
      clearSpeechStartWatchdog,
      markPageComplete,
      updateNarrationMode,
      updateStatus,
    ],
  );

  const playFormalAudio = useCallback(
    (page: ImmersiveKnowledgeNarrationPage, token: string) => {
      const source = page.formalAudio;
      if (!source?.src.trim()) {
        playSpeechPreview(page);
        return;
      }

      cancelNarration(false);
      updateNarrationMode("formal");
      setFormalAudioFallback(false);
      setActiveFormalCue(null);
      updateStatus("starting");

      let audio: HTMLAudioElement;
      try {
        audio = new Audio();
      } catch {
        playSpeechPreview(page, true);
        return;
      }

      const mimeType = source.mimeType?.trim();
      let mimeTypeSupported = true;
      try {
        if (mimeType && typeof audio.canPlayType === "function") {
          mimeTypeSupported = audio.canPlayType(mimeType) !== "";
        }
      } catch {
        mimeTypeSupported = false;
      }
      if (!mimeTypeSupported) {
        disposeFormalAudio(audio);
        playSpeechPreview(page, true);
        return;
      }

      try {
        audio.preload = "auto";
        audio.src = source.src;
        audio.volume = volumeRef.current;
      } catch {
        disposeFormalAudio(audio);
        playSpeechPreview(page, true);
        return;
      }
      audioRef.current = audio;

      let fallbackStarted = false;
      const fallbackToPreview = () => {
        if (
          fallbackStarted ||
          audioRef.current !== audio ||
          autoplayTargetRef.current?.token !== token
        ) {
          return;
        }

        fallbackStarted = true;
        playSpeechPreview(page, true);
      };
      formalAudioFailureRef.current = fallbackToPreview;

      const updateCue = () => {
        if (audioRef.current !== audio) return;
        setActiveFormalCue(formalNarrationCueAt(source.cues, audio.currentTime * 1_000));
      };

      audio.onplaying = () => {
        if (audioRef.current !== audio) return;
        clearFormalAudioStartWatchdog();
        updateCue();
        updateStatus("playing");
      };
      audio.onpause = () => {
        if (audioRef.current === audio && !audio.ended) updateStatus("paused");
      };
      audio.ontimeupdate = updateCue;
      audio.onseeked = updateCue;
      audio.onended = () => {
        if (audioRef.current !== audio) return;
        fallbackStarted = true;
        clearFormalAudioStartWatchdog();
        formalAudioFailureRef.current = null;
        audio.onerror = null;
        setActiveFormalCue(null);
        markPageComplete(page.id);
        updateStatus("ended");
      };
      audio.onerror = fallbackToPreview;

      formalAudioStartWatchdogRef.current = window.setTimeout(
        fallbackToPreview,
        formalAudioStartWatchdogMs,
      );

      try {
        audio.load();
        if (audioRef.current !== audio) return;
        const playResult = audio.play();
        if (playResult && typeof playResult.catch === "function") {
          void playResult.catch(fallbackToPreview);
        }
      } catch {
        fallbackToPreview();
      }
    },
    [
      cancelNarration,
      clearFormalAudioStartWatchdog,
      disposeFormalAudio,
      markPageComplete,
      playSpeechPreview,
      updateNarrationMode,
      updateStatus,
    ],
  );

  const playPage = useCallback(
    (page: ImmersiveKnowledgeNarrationPage) => {
      const target = autoplayTargetRef.current;
      if (hasFormalNarration(page) && target) {
        playFormalAudio(page, target.token);
        return;
      }
      playSpeechPreview(page);
    },
    [playFormalAudio, playSpeechPreview],
  );

  useEffect(() => {
    const target = autoplayTargetRef.current;

    // Stop the previous page synchronously. Deferring only the new playback call
    // lets React StrictMode discard its first development-only effect pass,
    // so a knowledge page is never narrated twice on entry.
    cancelNarration(false);
    if (!target || target.token !== autoplayToken) return;

    setFormalAudioFallback(false);
    updateNarrationMode(hasFormalNarration(target.page) ? "formal" : "preview");
    updateStatus(hasFormalNarration(target.page) || getSpeechRuntime() ? "idle" : "unavailable");

    const timeoutId = window.setTimeout(() => {
      if (autoplayTargetRef.current?.token !== autoplayToken) return;
      if (!pageLifecycleActiveRef.current || document.hidden) return;
      if (["starting", "playing", "paused"].includes(statusRef.current)) return;
      playPage(target.page);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      cancelNarration(false);
    };
  }, [autoplayToken, cancelNarration, playPage, updateNarrationMode, updateStatus]);

  useEffect(() => {
    const retryAfterGesturePrime = () => {
      if (narrationModeRef.current === "formal") return;
      if (!["idle", "starting", "blocked"].includes(statusRef.current)) return;
      const target = autoplayTargetRef.current;
      if (!target) return;
      playPage(target.page);
    };

    window.addEventListener(speechSynthesisGesturePrimedEvent, retryAfterGesturePrime);
    return () => {
      window.removeEventListener(speechSynthesisGesturePrimedEvent, retryAfterGesturePrime);
    };
  }, [playPage]);

  useEffect(() => {
    const retryOnGeneralGesture = () => {
      if (statusRef.current !== "blocked" || !pageLifecycleActiveRef.current) return;

      // This runs inside the original gesture stack. The global bridge normally
      // unlocks speech before scene entry; this local bridge covers direct
      // reloads and engines that silently ignored an earlier start request.
      primeSpeechSynthesisFromGesture();
      if (statusRef.current !== "blocked") return;

      const target = autoplayTargetRef.current;
      if (target) playPage(target.page);
    };

    window.addEventListener("pointerdown", retryOnGeneralGesture, { capture: true, passive: true });
    window.addEventListener("touchstart", retryOnGeneralGesture, { capture: true, passive: true });
    window.addEventListener("keydown", retryOnGeneralGesture, { capture: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", retryOnGeneralGesture, true);
      window.removeEventListener("touchstart", retryOnGeneralGesture, true);
      window.removeEventListener("keydown", retryOnGeneralGesture, true);
    };
  }, [playPage]);

  useEffect(() => {
    const stopForPageExit = () => {
      pageLifecycleActiveRef.current = false;
      cancelNarration(true);
    };
    const resumeAfterPageReturn = () => {
      if (pageLifecycleActiveRef.current || document.hidden) return;
      pageLifecycleActiveRef.current = true;
      const target = autoplayTargetRef.current;
      if (target) playPage(target.page);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopForPageExit();
        return;
      }
      resumeAfterPageReturn();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", stopForPageExit);
    window.addEventListener("pageshow", resumeAfterPageReturn);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", stopForPageExit);
      window.removeEventListener("pageshow", resumeAfterPageReturn);
    };
  }, [cancelNarration, playPage]);

  const pauseNarration = () => {
    try {
      if (narrationModeRef.current === "formal" && audioRef.current) {
        try {
          audioRef.current.pause();
        } catch {
          formalAudioFailureRef.current?.();
          return;
        }
        updateStatus("paused");
        return;
      }
      getSpeechRuntime()?.synthesis.pause();
      updateStatus("paused");
    } catch {
      updateStatus("error");
    }
  };

  const resumeNarration = () => {
    try {
      if (narrationModeRef.current === "formal" && audioRef.current) {
        const currentAudio = audioRef.current;
        const currentFailure = formalAudioFailureRef.current;
        const currentToken = autoplayTargetRef.current?.token;
        const failCurrentResume = () => {
          if (
            audioRef.current !== currentAudio ||
            autoplayTargetRef.current?.token !== currentToken ||
            formalAudioFailureRef.current !== currentFailure
          ) {
            return;
          }
          currentFailure?.();
        };
        clearFormalAudioStartWatchdog();
        formalAudioStartWatchdogRef.current = window.setTimeout(
          failCurrentResume,
          formalAudioStartWatchdogMs,
        );
        updateStatus("starting");
        let playResult: Promise<void> | undefined;
        try {
          playResult = currentAudio.play();
        } catch {
          failCurrentResume();
          return;
        }
        if (playResult && typeof playResult.catch === "function") {
          void playResult.catch(failCurrentResume);
        }
        return;
      }
      getSpeechRuntime()?.synthesis.resume();
      updateStatus("playing");
    } catch {
      updateStatus("error");
    }
  };

  const goToPage = (nextPageIndex: number) => {
    if (!currentPage) return;
    const nextPage = pages[nextPageIndex];
    if (!nextPage) return;

    cancelNarration(true);
    if (nextPageIndex > pageIndex) markPageComplete(currentPage.id);
    setCursor({ contentKey, pageIndex: nextPageIndex });
    onPageChange?.(nextPageIndex, nextPage);
  };

  const completeNode = () => {
    if (completionSentRef.current) return;
    if (currentPage) markPageComplete(currentPage.id);
    cancelNarration(true);
    completionSentRef.current = true;
    setCompletionSent(true);
    onComplete();
  };

  const goToPreviousKnowledgePoint = () => {
    if (!onPreviousKnowledgePoint) return;
    cancelNarration(true);
    onPreviousKnowledgePoint();
  };

  const progressLabel = useMemo(() => {
    if (pages.length === 0) return "暂无讲授页";
    return `第 ${pageIndex + 1} / ${pages.length} 页`;
  }, [pageIndex, pages.length]);

  return (
    <section
      className={`${styles.overlay} ${anchorClassNames[activeAnchor]} ${
        reducedMotion ? styles.reducedMotion : ""
      }`}
      data-content-key={contentKey}
      data-narration-anchor={activeAnchor}
      aria-label={title.trim() ? `${title}知识讲授` : "知识讲授"}
    >
      <article
        className={styles.card}
        data-formal-audio={
          formalAudioFallback ? "fallback" : formalAudioAvailable ? "ready" : "pending"
        }
      >
        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>{eyebrow}</span>
            {title.trim() ? (
              <h2
                className={Array.from(title.trim()).length > 14 ? styles.compactTitle : undefined}
              >
                {title}
              </h2>
            ) : null}
          </div>
          <span className={styles.progress}>{progressLabel}</span>
        </header>

        {currentPage ? (
          <>
            <div className={styles.page} key={`${contentKey}:${currentPage.id}`}>
              {currentPage.heading && currentPage.displayHeading !== false ? (
                <h3>{currentPage.heading}</h3>
              ) : null}
              <p>{currentPage.body}</p>
            </div>

            <div className={styles.previewNotice}>
              <span aria-hidden="true">声</span>
              <div>
                <strong>
                  {formalAudioFallback
                    ? "正式音轨播放失败·已切换系统语音预览"
                    : narrationMode === "formal"
                      ? "正式普通话音轨"
                      : "系统语音预览·本机朗读"}
                </strong>
                <p role="status" aria-live="polite">
                  {narrationMode === "formal" ? formalStatusCopy(status) : statusCopy(status)}
                </p>
                {narrationMode === "formal" && activeFormalCue ? (
                  <p
                    className={styles.formalCue}
                    data-formal-cue={activeFormalCue.id}
                    aria-live="polite"
                  >
                    {activeFormalCue.text}
                  </p>
                ) : null}
              </div>
            </div>

            <fieldset className={styles.audioControls}>
              <legend className={styles.visuallyHidden}>知识点朗读控制</legend>
              {status === "playing" ? (
                <button type="button" onClick={pauseNarration}>
                  暂停朗读
                </button>
              ) : status === "paused" ? (
                <button type="button" onClick={resumeNarration}>
                  继续朗读
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!narrationAvailable}
                  onClick={() => playPage(currentPage)}
                >
                  播放朗读
                </button>
              )}
              <button
                type="button"
                disabled={!narrationAvailable}
                onClick={() => playPage(currentPage)}
              >
                重播本页
              </button>
              <label className={styles.volumeControl}>
                <span>朗读音量</span>
                <input
                  type="range"
                  aria-label="朗读音量"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  disabled={!narrationAvailable}
                  onChange={(event) => setVolume(clampVolume(Number(event.currentTarget.value)))}
                />
                <output>{Math.round(volume * 100)}%</output>
              </label>
            </fieldset>

            <footer className={styles.navigation}>
              <button
                className={styles.previousKnowledgePoint}
                type="button"
                disabled={!onPreviousKnowledgePoint}
                onClick={goToPreviousKnowledgePoint}
              >
                上一个知识点
              </button>
              <ol className={styles.pageDots} aria-label="讲授页完成进度">
                {pages.map((page, index) => (
                  <li
                    key={page.id}
                    className={completedPageIds.has(page.id) ? styles.completedDot : ""}
                  >
                    <span className={styles.visuallyHidden}>{`第${index + 1}页${
                      completedPageIds.has(page.id) ? "已完成" : "未完成"
                    }`}</span>
                  </li>
                ))}
              </ol>
              <fieldset className={styles.pageActions}>
                <legend className={styles.visuallyHidden}>当前知识点翻页</legend>
                <button
                  type="button"
                  disabled={pageIndex === 0}
                  onClick={() => goToPage(pageIndex - 1)}
                >
                  上一页
                </button>
                {pageIndex < pages.length - 1 ? (
                  <button type="button" onClick={() => goToPage(pageIndex + 1)}>
                    下一页
                  </button>
                ) : (
                  <button type="button" disabled={completionSent} onClick={completeNode}>
                    {completionSent ? "本节点已完成" : "完成讲授并继续"}
                  </button>
                )}
              </fieldset>
            </footer>
          </>
        ) : (
          <div className={styles.emptyState}>
            <p>本知识节点暂无可展示页面，后续流程仍可继续。</p>
            <button type="button" disabled={completionSent} onClick={completeNode}>
              {completionSent ? "本节点已完成" : "继续下一项"}
            </button>
          </div>
        )}
      </article>
    </section>
  );
}
